import { useRef, useState } from "react";
import { Page, useCurrent } from "./common";
import { PageHead, Avatar, Empty } from "../components/ui";
import { Icon } from "../components/Icons";
import { actions } from "../lib/store";
import { useCan } from "../lib/auth";
import { isInterior } from "../lib/program";
import { fileToDataUri, averageHex } from "../lib/image";
import { storeImage } from "../lib/sync";
import { moodSwatches } from "../lib/ai";
import { fmtDate } from "../lib/format";

const MATERIALS = ["Timber", "Stone", "Plaster", "Metal", "Textile", "Paint", "Tile", "Glass", "Concrete"];

export default function Moodboard() {
  const p = useCurrent();
  const canEdit = useCan("brief");
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [sw, setSw] = useState({ name: "", hex: "#c9bda6", material: "Stone" });
  if (!p) return null;
  const mb = p.moodboard ?? { images: [], swatches: [] };
  const interior = isInterior(p.type);

  async function onFiles(files: FileList | null) {
    if (!files?.length) return;
    setBusy(true); setMsg(null);
    for (const f of Array.from(files).slice(0, 12)) {
      if (!f.type.startsWith("image/")) continue;
      try { const src = await storeImage(await fileToDataUri(f)); actions.addMoodImage(p!.id, src, "", "team", "Studio"); } catch {}
    }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  }

  async function developAI() {
    const raster = mb.images.map((i) => i.src).filter((s) => /^data:image\/(jpeg|png|webp);base64,/.test(s) || /\/api\/img\//.test(s));
    if (!raster.length) { setMsg("Add photo images to the mood board first — AI develops swatches from real photos."); return; }
    setAiBusy(true); setMsg(null);
    const res = await moodSwatches(p!.type, raster);
    setAiBusy(false);
    if (!res.ok || !res.swatches?.length) { setMsg(res.reason === "no-key" ? "AI isn't enabled yet — set ANTHROPIC_API_KEY." : res.reason === "no-raster" ? "Add photo images (not just the seeded panels) to develop swatches with AI." : "Couldn't reach the AI service."); return; }
    actions.addSwatches(p!.id, res.swatches);
    setMsg(`Developed ${res.swatches.length} swatches from the mood board.`);
  }

  async function pullColour(src: string) {
    const hex = await averageHex(src);
    actions.addSwatch(p!.id, { name: "New swatch", hex, material: "Paint" });
  }

  return (
    <Page>
      <PageHead kicker={`${p.name} · ${interior ? "Interiors" : "Design"}`} title={interior ? "Interior mood board" : "Mood board"}>
        {canEdit && <button className="btn ghost sm" onClick={() => fileRef.current?.click()} disabled={busy}><Icon.image width={15} />{busy ? "Adding…" : "Add images"}</button>}
        <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: "none" }} onChange={(e) => onFiles(e.target.files)} />
      </PageHead>
      <p className="muted reveal" style={{ maxWidth: 640, marginBottom: 22 }}>
        The client shares images they're drawn to; from that mood we develop a coherent <b>feature swatch board</b> — the material &amp; finish palette that carries through the {interior ? "interiors" : "design"}.
      </p>
      {msg && <div className="card reveal" style={{ padding: "12px 16px", marginBottom: 18, fontSize: 13.5, borderColor: "color-mix(in srgb, var(--accent) 40%, var(--line))" }}>{msg}</div>}

      {/* mood images */}
      <div className="row between reveal" style={{ marginBottom: 14 }}><h3 className="serif" style={{ fontSize: 22 }}>Mood board</h3><span className="dim" style={{ fontSize: 13 }}>{mb.images.length} images</span></div>
      {mb.images.length === 0 ? (
        <Empty>No images yet. {canEdit ? "Add some, or the client can share from their portal." : ""}</Empty>
      ) : (
        <div className="reveal" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))", gap: 14, marginBottom: 30 }}>
          {mb.images.map((im) => (
            <div key={im.id} className="card hoverable" style={{ overflow: "hidden" }}>
              <div style={{ position: "relative", aspectRatio: "4/3", background: "var(--paper-2)" }}>
                <img src={im.src} alt={im.caption || "mood"} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                {canEdit && <button className="iconbtn" style={{ position: "absolute", top: 8, right: 8, width: 28, height: 28, background: "rgba(255,255,255,.85)" }} onClick={() => actions.removeMoodImage(p.id, im.id)}><Icon.close width={13} /></button>}
                {canEdit && <button className="btn soft sm" style={{ position: "absolute", bottom: 8, left: 8, padding: "4px 9px", fontSize: 11 }} onClick={() => pullColour(im.src)} title="Pull a colour swatch from this image"><Icon.concept width={12} />Pull colour</button>}
              </div>
              <div style={{ padding: "9px 12px" }}>
                <div style={{ fontSize: 12.5 }}>{im.caption || "Untitled"}</div>
                <div className="row" style={{ gap: 6, marginTop: 4 }}><Avatar name={im.addedByName} hue={im.addedBy === "client" ? p.client.avatarHue : 20} size={18} /><span className="dim" style={{ fontSize: 10.5 }}>{im.addedBy === "client" ? "Client" : im.addedByName} · {fmtDate(im.addedOn)}</span></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* feature swatch board */}
      <div className="row between reveal" style={{ marginBottom: 14 }}>
        <div><div className="kicker">Developed from the mood</div><h3 className="serif" style={{ fontSize: 22, marginTop: 4 }}>Feature swatch board</h3></div>
        {canEdit && <button className="btn accent sm" onClick={developAI} disabled={aiBusy}><Icon.spark width={15} />{aiBusy ? "Developing…" : "Develop with AI"}</button>}
      </div>

      <div className="card pad reveal">
        {mb.swatches.length === 0 && <div className="dim" style={{ fontSize: 13.5, marginBottom: 14 }}>No swatches yet. Develop them with AI, or add your own below.</div>}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(150px,1fr))", gap: 14 }}>
          {mb.swatches.map((s) => (
            <div key={s.id} className="panel" style={{ overflow: "hidden", padding: 0 }}>
              <div style={{ height: 72, background: s.hex, position: "relative" }}>
                {canEdit && <button className="iconbtn" style={{ position: "absolute", top: 6, right: 6, width: 24, height: 24, background: "rgba(255,255,255,.8)" }} onClick={() => actions.removeSwatch(p.id, s.id)}><Icon.close width={12} /></button>}
              </div>
              <div style={{ padding: "10px 12px" }}>
                <div style={{ fontWeight: 600, fontSize: 13.5 }}>{s.name}</div>
                <div className="row between" style={{ marginTop: 3 }}><span className="dim" style={{ fontSize: 11.5 }}>{s.material || "—"}</span><span className="mono dim" style={{ fontSize: 10.5 }}>{s.hex}</span></div>
                {s.note && <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>{s.note}</div>}
              </div>
            </div>
          ))}
        </div>

        {canEdit && (
          <div className="row wrap" style={{ gap: 10, marginTop: 18, alignItems: "flex-end", borderTop: "1px solid var(--line)", paddingTop: 16 }}>
            <input type="color" value={sw.hex} onChange={(e) => setSw({ ...sw, hex: e.target.value })} style={{ width: 44, height: 40, border: "1px solid var(--line-2)", borderRadius: 10, background: "none", cursor: "pointer" }} />
            <div className="field" style={{ flex: 1, minWidth: 160 }}><label>Swatch name</label><input className="input" value={sw.name} onChange={(e) => setSw({ ...sw, name: e.target.value })} placeholder="e.g. Brushed brass" /></div>
            <div className="field" style={{ width: 140 }}><label>Material</label><select className="select" value={sw.material} onChange={(e) => setSw({ ...sw, material: e.target.value })}>{MATERIALS.map((m) => <option key={m}>{m}</option>)}</select></div>
            <button className="btn soft" onClick={() => { if (!sw.name.trim()) return; actions.addSwatch(p.id, { name: sw.name.trim(), hex: sw.hex, material: sw.material }); setSw({ name: "", hex: "#c9bda6", material: "Stone" }); }}><Icon.plus width={15} />Add swatch</button>
          </div>
        )}
      </div>
    </Page>
  );
}
